import React from "react";
import "./Footer.css";
import { FaMapMarkerAlt, FaPhoneVolume, FaEnvelope, FaFacebookF, FaInstagram, FaYoutube, FaLinkedinIn } from "react-icons/fa";
import { Link } from "react-router-dom"; 
import { useScrollNavigation } from "../hooks/useScrollNavigation";
import { Helmet } from "react-helmet-async";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { navigateToSection } = useScrollNavigation();

  // App Metadata for Google SEO Mobile App Association
  const appSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Aikyam Sports",
    "operatingSystem": "Android, iOS",
    "applicationCategory": "SportsApplication",
    "description":
      "Aikyam Sports is the official app for Udupi's No. 1 Sports Science and Best Physiotherapy Center, offering top-rated athlete monitoring, performance analysis, and fitness training.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "INR"
    },
    "downloadUrl": [
      "https://apps.apple.com/us/app/aikyam-sports/id6757456836",
      "https://play.google.com/store/apps/details?id=com.aikyamsports.app"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "Aikyam Sports Science",
      "url": "https://aikyamsports.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://aikyamsports.com/logo11.png"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+916363202281",
        "contactType": "customer service",
        "areaServed": "IN"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Center for Sports Science, Mahatma Gandhi Stadium, Ajjarakad",
        "addressLocality": "Udupi",
        "addressRegion": "Karnataka",
        "postalCode": "576101",
        "addressCountry": "IN"
      },
      "sameAs": [
        "https://www.facebook.com/aikyamsportsscience/",
        "https://www.instagram.com/aikyamsportsscience/",
        "https://www.youtube.com/@AikyamSports",
        "https://www.linkedin.com/company/aikyam-sports-science/"
      ]
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SportsActivityLocation", // Google also recognizes "HealthClub" / "LocalBusiness"
    "name": "Aikyam Sports Science",
    "description": "Ranked as the No. 1 Physiotherapy Clinic and Best Sports Rehab Center in Udupi, providing elite strength and conditioning to athletes in Udupi, Manipal, and Mangalore.",
    "image": "https://aikyamsports.com/logo11.png",
    "url": "https://aikyamsports.com",
    "telephone": "+916363202281",
    "priceRange": "₹₹",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Center for Sports Science, Mahatma Gandhi Stadium, Ajjarakad, Brahmagiri",
      "addressLocality": "Udupi",
      "addressRegion": "Karnataka",
      "postalCode": "576101",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 13.332010191106345,
      "longitude": 74.74222145830171
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
        "opens": "06:00",
        "closes": "20:00"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/aikyamsportsscience/",
      "https://www.instagram.com/aikyamsportsscience/",
      "https://www.youtube.com/@AikyamSports",
      "https://www.linkedin.com/company/aikyam-sports-science/"
    ]
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(appSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      </Helmet>
      <footer className="footer-section" id="footer">
        <div className="footer-content">
          {/* Left Side */}
          <div className="footer-left">
            <div className="footer-top">
              <img src={require("../assets/logo1.png")} alt="Aikyam Logo" className="footer-logo" />
              <p className="footer-about-main">
                <strong>AIKYAM Sports Science: Udupi's Best Physiotherapy Center & No. 1 Sports Rehab Center.</strong> We elevate sports tech to empower athletes with top-rated strength and conditioning, recovery, and psychology. We are dedicated to delivering the best evidence-based care for athletes in Udupi, Manipal, and Mangalore.
              </p>
            </div>

            <p className="footer-about-sub">
              As the region's leading and best-rated facility, we are proud recipients of the NIDHI-EIR and NIDHI-PRAYAS grants from the Government of India, recognizing our commitment to innovation in sports science.
            </p>

            <div className="footer-apps">
              <a href="https://apps.apple.com/us/app/aikyam-sports/id6757456836"
                 target="_blank" 
                 rel="noopener noreferrer">
                <img src={require("../assets/appstore.png")} alt="App Store" className="store-icon" />
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.aikyamsports.app&hl=en_IN"
                 target="_blank"
                 rel="noopener noreferrer">
                <img src={require("../assets/playstore.png")} alt="Play Store" className="store-icon2" />
              </a>
            </div>
            
            <div className="dev-link-container" style={{ marginTop: "10px" }}>
              <a 
                href="https://play.google.com/store/apps/dev?id=7536128928972382360&hl=en_IN" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm hover:underline"
                style={{ color: "var(--footer-text-color, #fff)", opacity: 0.8 }}
              >
                View More on Google Play
              </a>
            </div>
          </div>

          {/* Right Side */}
          <div className="footer-links">
            <div className="footer-column">
              <h4>Company</h4>
              <ul>
                <li><Link to="/about">About Us</Link></li>
                <li><Link to="/fitness">Our Best Services</Link></li>
                <li><Link to="/timing-gates">Our Products</Link></li>
                <li><Link to="/blog">Blog</Link></li>
                <li><Link to="/contact">Contact Us</Link></li>
                <li><Link to="/terms">Terms & Conditions</Link></li>
                <li><Link to="/privacy">Privacy Policy</Link></li>
                <li><Link to="/refund-policy">Refund Policy</Link></li>
                <li><Link to="/cancellation-policy">Cancellation Policy</Link></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Resources</h4>
              <ul>
                <li onClick={() => navigateToSection("athletes")}>Our Athletes</li>
                <li onClick={() => navigateToSection("team")}>Our Team</li>
                <li>      
                  <a 
                    href="https://www.youtube.com/@AikyamSports/shorts" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-inherit no-underline hover:underline hover:text-orange-500">
                    Workout Videos
                  </a>
                </li>
                <li>
                  <a 
                    href="https://play.google.com/store/apps/dev?id=7536128928972382360&hl=en_IN" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-inherit no-underline hover:underline hover:text-orange-500">
                    Our Play Store Apps
                  </a>
                </li>
                <li><Link to="/fitness">Fitness Service</Link></li>
                <li><Link to="/athlete-management-system">Athlete Management System</Link></li>
                <li onClick={() => navigateToSection("faq")}>FAQ</li>
                <li onClick={() => navigateToSection("success")}>Success Stories</li>
                <li><Link to="/membership">Membership / Packages</Link></li>
              </ul>
            </div>

            <div className="footer-column">
              <h4>Get In Touch</h4>
              <ul className="footer-contact-list">
                <li className="footer-contact-item">
                  <span className="contact-icon"><FaMapMarkerAlt /></span>
                  <span className="contact-text">
                    <a 
                      href="https://maps.app.goo.gl/snFnjc3b4GVYZs369" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Center for Sports Science, Mahatma Gandhi Stadium, Ajjarakad, Brahmagiri, Udupi, Karnataka - 576101
                    </a> 
                  </span>
                </li>
                <div className="footer-map" itemScope itemType="https://schema.org/LocalBusiness">
                  <meta itemProp="name" content="Aikyam Sports Science - No 1 Physiotherapy" />
                  <meta itemProp="address" content="Center for Sports Science, Mahatma Gandhi Stadium, Ajjarakad, Udupi, Karnataka - 576101" />
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d336.29219818079844!2d74.74222145830171!3d13.332010191106345!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bbcbb4520a93543%3A0x4f53cf7126b9e6dd!2sCentre%20for%20Sports%20Science%2C%20Department%20of%20Youth%20Empowerment%20%26%20Sport!5e1!3m2!1sen!2sin!4v1783680453332!5m2!1sen!2sin"
                    width="100%"
                    height="220"
                    style={{ border: 0, borderRadius: "12px" }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                    title="Aikyam Sports Science Location"
                  />
                </div>
                <li className="footer-contact-item" id="contact-numbers">
                  <span className="contact-icon"><FaPhoneVolume /></span>
                  <span className="contact-text">+916363202281<br />+917019386664</span>
                </li>
                <li className="footer-contact-item">
                  <span className="contact-icon"><FaEnvelope /></span>
                  <span className="contact-text">aikyamsports@gmail.com</span>
                </li>
                <ul className="footer-socials">
                  <li>
                    <a href="https://www.facebook.com/p/Aikyam-Sports-Science-61558692161413/ " target="_blank" rel="noopener noreferrer">
                      <FaFacebookF />
                    </a>
                  </li>
                  <li>
                    <a href="https://www.instagram.com/aikyamsportsscience/" target="_blank" rel="noopener noreferrer">
                      <FaInstagram />
                    </a>
                  </li>
                  <li>
                    <a href="https://www.youtube.com/watch?v=mkshyk__zSY" target="_blank" rel="noopener noreferrer">
                      <FaYoutube />
                    </a>
                  </li>
                  <li>
                    <a href="https://www.linkedin.com/company/aikyam-sports-science/" target="_blank" rel="noopener noreferrer">
                      <FaLinkedinIn />
                    </a>
                  </li>
                </ul>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="footer-bottom">
          © {currentYear} Aikyam. All rights reserved.
        </div>
      </footer>
    </>
  );
};

export default Footer;